import {Injectable} from '@angular/core';
import {BehaviorSubject, finalize, forkJoin, Observable, of, switchMap, tap} from 'rxjs';
import {TranslateService} from '@ngx-translate/core';
import {DataManipulationService} from 'src/app/services/data-manipulation.service';
import {
  PyramidCategory,
  PyramidLegendItem
} from 'src/app/components/categories-pyramid-display/categories-pyramid-display.component';
import {TeamDynamicService} from '../services/team-dynamic.service';
import {TeamDynamicRequestDto} from '../dto/team-dynamic-request.dto';
import {
  ActiveTeamDynamicVm,
  IndicatorDetailVm,
  IndicatorTabVm,
  OverallScoreVm,
  ScoreDevelopmentChipVm,
  ScoreDevelopmentVm,
  StatementResponsesVm,
  StatementsVm,
  StatementVm,
  TeamOverviewVm
} from '../models/team-dynamic.vm';
import {
  mapActiveTeamDynamic,
  mapIndicatorDetail,
  mapIndicatorTabs,
  mapOverallScore,
  mapPyramidCategories,
  mapScoreDevelopment,
  mapScoreDevelopmentChips,
  mapStatements,
  mapTeamOverview
} from '../mappers/team-dynamic.mapper';

@Injectable({
  providedIn: 'root'
})
export class TeamDynamicFacade {
  public intervals: any[] = [];

  private activeTeamDynamic: ActiveTeamDynamicVm | null = null;
  private pendingCategoryUuid: string | null = null;
  private selectedCategoryUuid: string | null = null;

  private loadingSubject = new BehaviorSubject<boolean>(false);
  readonly loading$ = this.loadingSubject.asObservable();
  private overallScoreSubject = new BehaviorSubject<OverallScoreVm | null>(null);
  readonly overallScore$ = this.overallScoreSubject.asObservable();
  private pyramidCategoriesSubject = new BehaviorSubject<PyramidCategory[]>([]);
  readonly pyramidCategories$ = this.pyramidCategoriesSubject.asObservable();
  private pyramidLegendSubject = new BehaviorSubject<PyramidLegendItem[]>([]);
  readonly pyramidLegend$ = this.pyramidLegendSubject.asObservable();
  private scoreDevelopmentSubject = new BehaviorSubject<ScoreDevelopmentVm>({labels: [], values: []});
  readonly scoreDevelopment$ = this.scoreDevelopmentSubject.asObservable();
  private loadingScoreDevelopmentSubject = new BehaviorSubject<boolean>(false);
  readonly loadingScoreDevelopment$ = this.loadingScoreDevelopmentSubject.asObservable();
  private scoreDevelopmentChipsSubject = new BehaviorSubject<ScoreDevelopmentChipVm[]>([]);
  readonly scoreDevelopmentChips$ = this.scoreDevelopmentChipsSubject.asObservable();
  private teamOverviewSubject = new BehaviorSubject<TeamOverviewVm | null>(null);
  readonly teamOverview$ = this.teamOverviewSubject.asObservable();
  private loadingTeamOverviewSubject = new BehaviorSubject<boolean>(false);
  readonly loadingTeamOverview$ = this.loadingTeamOverviewSubject.asObservable();
  private indicatorTabsSubject = new BehaviorSubject<IndicatorTabVm[]>([]);
  readonly indicatorTabs$ = this.indicatorTabsSubject.asObservable();
  private selectedCategoryUuidSubject = new BehaviorSubject<string | null>(null);
  readonly selectedCategoryUuid$ = this.selectedCategoryUuidSubject.asObservable();
  private indicatorDetailSubject = new BehaviorSubject<IndicatorDetailVm | null>(null);
  readonly indicatorDetail$ = this.indicatorDetailSubject.asObservable();
  private indicatorScoreDevelopmentSubject = new BehaviorSubject<ScoreDevelopmentVm>({labels: [], values: []});
  readonly indicatorScoreDevelopment$ = this.indicatorScoreDevelopmentSubject.asObservable();
  private loadingIndicatorSubject = new BehaviorSubject<boolean>(false);
  readonly loadingIndicator$ = this.loadingIndicatorSubject.asObservable();
  private statementsSubject = new BehaviorSubject<StatementsVm | null>(null);
  readonly statements$ = this.statementsSubject.asObservable();
  private loadingStatementsSubject = new BehaviorSubject<boolean>(false);
  readonly loadingStatements$ = this.loadingStatementsSubject.asObservable();
  private statementResponsesSubject = new BehaviorSubject<StatementResponsesVm | null>(null);
  readonly statementResponses$ = this.statementResponsesSubject.asObservable();
  private teamVisibilitySubject = new BehaviorSubject<boolean>(false);
  readonly teamVisibility$ = this.teamVisibilitySubject.asObservable();

  constructor(private teamDynamicApi: TeamDynamicService, private data: DataManipulationService,
              private translate: TranslateService) {
  }

  loadData(payload: TeamDynamicRequestDto, initialCategoryUuid?: string | null): void {
    this.loadingSubject.next(true);
    this.resetIndicatorState();
    this.pendingCategoryUuid = initialCategoryUuid ?? null;
    this.setPyramidLegend();

    forkJoin({
      intervals: this.teamDynamicApi.getIndicatorInterval(),
      active: this.teamDynamicApi.getActiveWorkspaceTeamDynamics()
    }).pipe(
      switchMap((res: any) => {
        this.intervals = [...(res.intervals ?? [])];
        this.activeTeamDynamic = mapActiveTeamDynamic(res.active);
        this.teamVisibilitySubject.next(!!this.activeTeamDynamic?.teamVisibility);
        return this.loadReports(payload);
      }),
      tap((res: any) => {
        this.pushReports(res.workspace, res.teams, res.scores);
        this.data.setIndicatorTeam(res.teams.teamTeamDynamics[0].team.uuid)
      }),
      finalize(() => this.loadingSubject.next(false)),
      switchMap(() => this.loadIndicatorDetail$(payload))
    ).subscribe();
  }

  reloadForPeriod(payload: TeamDynamicRequestDto): void {
    this.loadingTeamOverviewSubject.next(true);
    this.loadingScoreDevelopmentSubject.next(true);

    this.loadReports(payload).pipe(
      tap((res: any) => this.pushReports(res.workspace, res.teams, res.scores)),
      finalize(() => {
        this.loadingTeamOverviewSubject.next(false);
        this.loadingScoreDevelopmentSubject.next(false);
      }),
      switchMap(() => this.loadIndicatorDetail$(payload))
    ).subscribe();
  }

  reloadTeamOverview(payload: TeamDynamicRequestDto): void {
    this.loadingTeamOverviewSubject.next(true);

    this.teamDynamicApi.getTeamsTeamDynamic(this.getTeamsOptions(payload)).pipe(
      finalize(() => this.loadingTeamOverviewSubject.next(false))
    ).subscribe((res: any) => {
      const teamsReport = res && !res.error ? res : null;
      this.teamOverviewSubject.next(
        mapTeamOverview(teamsReport, this.pyramidCategoriesSubject.value, this.intervals, this.data.colors)
      );
    });
  }

  loadScoreDevelopment(payload: TeamDynamicRequestDto): void {
    this.loadingScoreDevelopmentSubject.next(true);

    this.teamDynamicApi.getTeamDynamicDevelopmentScores(this.getScoreDevelopmentOptions(payload)).pipe(
      finalize(() => this.loadingScoreDevelopmentSubject.next(false))
    ).subscribe((res: any) => {
      this.scoreDevelopmentSubject.next(mapScoreDevelopment(res));
    });
  }

  private loadReports(payload: TeamDynamicRequestDto): Observable<any> {
    return forkJoin({
      workspace: this.teamDynamicApi.getWorkspaceTeamDynamic(payload.startDate, payload.endDate),
      teams: this.teamDynamicApi.getTeamsTeamDynamic(this.getTeamsOptions(payload)),
      scores: this.teamDynamicApi.getTeamDynamicDevelopmentScores(this.getScoreDevelopmentOptions(payload))
    });
  }

  private pushReports(workspace: any, teams: any, scores: any): void {
    const workspaceReport = workspace && !workspace.error ? workspace : null;
    const teamsReport = teams && !teams.error ? teams : null;
    const categories = mapPyramidCategories(workspaceReport, this.intervals, this.data.colors);
    const teamOverview = mapTeamOverview(teamsReport, categories, this.intervals, this.data.colors);

    const overallScore = mapOverallScore(workspaceReport, teamOverview.rows.length,
      this.intervals, this.data.colors);

    this.pyramidCategoriesSubject.next(categories);
    this.indicatorTabsSubject.next(mapIndicatorTabs(categories));
    this.resolveSelectedCategory(categories);
    this.scoreDevelopmentChipsSubject.next(
      mapScoreDevelopmentChips(categories, this.translate.instant('td_overall_score'), overallScore)
    );
    this.overallScoreSubject.next(overallScore);
    this.teamOverviewSubject.next(teamOverview);
    this.scoreDevelopmentSubject.next(mapScoreDevelopment(scores));
  }

  openIndicatorView(payload: TeamDynamicRequestDto): void {
    if (this.selectedCategoryUuid) {
      return;
    }
    const firstCategoryUuid = this.pyramidCategoriesSubject.value[0]?.uuid;
    if (firstCategoryUuid) {
      this.selectIndicatorCategory(firstCategoryUuid, payload);
    }
  }

  selectIndicatorCategory(collectionUuid: string, payload: TeamDynamicRequestDto): void {
    if (this.selectedCategoryUuid === collectionUuid && this.indicatorDetailSubject.value) {
      return;
    }
    this.selectedCategoryUuid = collectionUuid;
    this.selectedCategoryUuidSubject.next(collectionUuid);
    this.loadIndicatorDetail$(payload).subscribe();
  }

  private resetIndicatorState(): void {
    this.selectedCategoryUuid = null;
    this.selectedCategoryUuidSubject.next(null);
    this.indicatorDetailSubject.next(null);
    this.indicatorScoreDevelopmentSubject.next({labels: [], values: []});
    this.statementsSubject.next(null);
    this.statementResponsesSubject.next(null);
  }

  private loadIndicatorDetail$(payload: TeamDynamicRequestDto): Observable<any> {
    const collectionUuid = this.selectedCategoryUuid;
    if (!collectionUuid) {
      return of(null);
    }
    this.loadingIndicatorSubject.next(true);

    return forkJoin({
      detail: this.teamDynamicApi.getWorkspaceTeamDynamicDetail(collectionUuid, payload.startDate, payload.endDate),
      scores: this.teamDynamicApi.getTeamDynamicDevelopmentScores({
        startDate: payload.startDate,
        endDate: payload.endDate,
        timedQuestionCollectionUuid: collectionUuid
      }),
      statements: this.teamDynamicApi.getQuestionWorkspaceTeamDynamic(collectionUuid,
        this.getStatementsOptions(payload))
    }).pipe(
      tap((res: any) => {
        this.indicatorDetailSubject.next(
          mapIndicatorDetail(res.detail, this.intervals, this.data.colors, this.activeTeamDynamic)
        );
        this.indicatorScoreDevelopmentSubject.next(mapScoreDevelopment(res.scores));
        this.pushStatements(res.statements);
      }),
      finalize(() => this.loadingIndicatorSubject.next(false))
    );
  }

  loadStatements(payload: TeamDynamicRequestDto): void {
    const collectionUuid = this.selectedCategoryUuid;
    if (!collectionUuid) {
      return;
    }
    this.loadingStatementsSubject.next(true);
    this.statementResponsesSubject.next(null);

    this.teamDynamicApi.getQuestionWorkspaceTeamDynamic(collectionUuid, this.getStatementsOptions(payload)).pipe(
      finalize(() => this.loadingStatementsSubject.next(false))
    ).subscribe((res: any) => this.pushStatements(res));
  }

  loadStatementResponses(statement: StatementVm): void {
    if (!statement?.questionUuid) {
      this.statementResponsesSubject.next(null);
      return;
    }
    this.statementResponsesSubject.next(null);

    const request$ = statement.type === 'single'
      ? this.teamDynamicApi.getStatementScaledResponses(statement.questionUuid, this.selectedCategoryUuid)
      : statement.type === 'scaled'
        ? (statement.score === null
          ? null
          : this.teamDynamicApi.getStatementScaledResponses(statement.questionUuid, this.selectedCategoryUuid))
        : statement.type === 'open'
          ? this.teamDynamicApi.getStatementOpenResponses(statement.questionUuid, 1, 6)
          : null;

    if (!request$) {
      return;
    }

    request$.subscribe((res: any) => {
      this.statementResponsesSubject.next(res && !res.error ? {
        questionUuid: statement.questionUuid,
        type: statement.type,
        payload: res,
        totalItems: res._total_items ?? 0,
        pageCount: res._page_count ?? 0
      } : null);
    });
  }

  setTeamVisibility(teamVisibility: boolean): void {
    const active = this.activeTeamDynamic;
    if (!active?.uuid) {
      return;
    }
    this.teamDynamicApi.patchWorkspaceTeamDynamicVisibility(active.uuid, teamVisibility)
      .subscribe((res: any) => {
        if (res && !res.error) {
          this.activeTeamDynamic = {...active, teamVisibility};
          this.teamVisibilitySubject.next(teamVisibility);
          return;
        }
        this.teamVisibilitySubject.next(active.teamVisibility);
      });
  }

  private pushStatements(response: any): void {
    this.statementsSubject.next(
      mapStatements(response, this.intervals, this.data.colors)
    );
  }

  private getStatementsOptions(payload: TeamDynamicRequestDto) {
    return {
      sort: payload.statementsSort,
      startDate: payload.statementsStartDate,
      endDate: payload.statementsEndDate
    };
  }

  private resolveSelectedCategory(categories: PyramidCategory[]): void {
    const requested = this.pendingCategoryUuid ?? this.selectedCategoryUuid;
    this.pendingCategoryUuid = null;
    if (!requested) {
      return;
    }
    const stillExists = categories.some(category => category.uuid === requested);
    this.selectedCategoryUuid = stillExists ? requested : (categories[0]?.uuid ?? null);
    this.selectedCategoryUuidSubject.next(this.selectedCategoryUuid);
  }

  private getTeamsOptions(payload: TeamDynamicRequestDto) {
    return {
      startDate: payload.startDate,
      endDate: payload.endDate,
      timedQuestionCollectionUuid: payload.teamsCollectionUuid ?? undefined,
      sort: payload.teamsSort
    };
  }

  private getScoreDevelopmentOptions(payload: TeamDynamicRequestDto) {
    return {
      startDate: payload.startDate,
      endDate: payload.endDate,
      timedQuestionCollectionUuid: payload.timedQuestionCollectionUuid ?? undefined
    };
  }

  private setPyramidLegend(): void {
    this.pyramidLegendSubject.next([
      {label: this.translate.instant('wo_team_dynamic_legend_strong'), color: this.data.colors.green},
      {label: this.translate.instant('wo_team_dynamic_legend_moderate'), color: this.data.colors.yellow},
      {label: this.translate.instant('wo_team_dynamic_legend_attention'), color: this.data.colors.orange},
      {label: this.translate.instant('wo_team_dynamic_legend_critical'), color: this.data.colors.red}
    ]);
  }
}
